import { useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Car, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const QUICK = [15, 30, 45, 60];
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

/* "On my way" for the field portal — one tap (optionally with a quick ETA). Shown while this engineer hasn't arrived;
   once sent it turns into a quiet "On the way since HH:MM" chip. `current` = my_attendance {en_route_at, eta_time}. */
export default function OnMyWayButton({ entityType, entityId, current, onSaved, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  if (current?.en_route_at) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-200" data-testid="in-house-on-my-way-sent">
        <CheckCircle size={16} weight="fill" /> On the way since {fmtTime(current.en_route_at)}{current.eta_time ? ` · ETA ${current.eta_time}` : ""}
      </span>
    );
  }
  const send = async (mins) => {
    setSaving(true);
    try {
      const { data } = await API.post(`/in-house-portal/${entityType}/${entityId}/on-my-way`, mins ? { eta_minutes: mins } : {});
      toast.success(`Marked as on the way${data.eta_time ? ` — ETA ${data.eta_time}` : ""}${data.requester_emailed ? " · requester emailed" : ""}`);
      setOpen(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled || saving} data-testid="in-house-on-my-way-btn"><Car className="h-4 w-4 mr-2" /> On my way</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 space-y-2" data-testid="in-house-on-my-way-popover">
        <p className="text-xs text-muted-foreground">Let the office and the requester know you've set off. Add a rough arrival time if you like.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {QUICK.map((m) => <Button key={m} size="sm" variant="outline" className="h-8 text-xs" disabled={saving} onClick={() => send(m)} data-testid={`in-house-on-my-way-${m}`}>~{m} min</Button>)}
        </div>
        <Button size="sm" className="w-full h-8 text-xs" disabled={saving} onClick={() => send(null)} data-testid="in-house-on-my-way-now">{saving ? "Sending…" : "On my way (no ETA)"}</Button>
      </PopoverContent>
    </Popover>
  );
}
